import { FormEvent, useEffect, useState } from 'react';
import { uploadImage } from '../api/client';
import { ProjectInfo } from '../api/types';
import { Button } from './components/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/dialog';
import { Input } from './components/input';
import { Label } from './components/label';
import { Switch } from './components/switch';
import { Textarea } from './components/textarea';

export interface ProjectFormValues {
  title_en: string;
  title_tr: string;
  description_en: string;
  description_tr: string;
  link_url: string;
  image_urls: string[];
  sort_order: number;
  is_active: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: ProjectInfo | null;
  onSubmit: (values: ProjectFormValues) => Promise<void>;
}

const emptyValues: ProjectFormValues = {
  title_en: '',
  title_tr: '',
  description_en: '',
  description_tr: '',
  link_url: '',
  image_urls: [],
  sort_order: 0,
  is_active: true,
};

function ProjectForm({ open, onOpenChange, initial, onSubmit }: Props) {
  const [values, setValues] = useState<ProjectFormValues>(initial ?? emptyValues);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setValues(initial ?? emptyValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const set = <K extends keyof ProjectFormValues>(key: K, val: ProjectFormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: val }));

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const { url } = await uploadImage(file);
      set('image_urls', [...values.image_urls, url]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Yükleme başarısız');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (idx: number) => {
    set('image_urls', values.image_urls.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Sadece http/https şemasına izin verilir — file://, javascript: gibi
    // adresler burada tıklayan ziyaretçinin tarayıcısında (public sitede
    // <a href> olarak) açılabileceği için tehlikeli/anlamsız olur.
    if (!/^https?:\/\//i.test(values.link_url.trim())) {
      setError('Bağlantı URL http:// veya https:// ile başlamalı');
      return;
    }

    setSaving(true);
    try {
      await onSubmit(values);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) { setValues(initial ?? emptyValues); onOpenChange(o); } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initial ? 'Projeyi Düzenle' : 'Yeni Proje'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="title_en">Başlık (EN)</Label>
              <Input id="title_en" value={values.title_en} onChange={(e) => set('title_en', e.target.value)} required />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="title_tr">Başlık (TR)</Label>
              <Input id="title_tr" value={values.title_tr} onChange={(e) => set('title_tr', e.target.value)} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="description_en">Açıklama (EN)</Label>
              <Textarea id="description_en" value={values.description_en} onChange={(e) => set('description_en', e.target.value)} required />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="description_tr">Açıklama (TR)</Label>
              <Textarea id="description_tr" value={values.description_tr} onChange={(e) => set('description_tr', e.target.value)} required />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="link_url">Bağlantı URL</Label>
            <Input id="link_url" value={values.link_url} onChange={(e) => set('link_url', e.target.value)} required type="url" />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Görseller</Label>
            <div className="flex flex-wrap gap-2">
              {values.image_urls.map((url, idx) => (
                <div key={url} className="relative">
                  <img src={url} alt="" className="h-16 w-16 rounded object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="absolute -right-1 -top-1 appearance-none rounded-full border-0 bg-[hsl(var(--destructive))] px-1.5 text-xs text-white cursor-pointer"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <Input type="file" accept="image/*" onChange={handleFileChange} disabled={uploading} />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sort_order">Sıra</Label>
              <Input
                id="sort_order"
                type="number"
                value={values.sort_order}
                onChange={(e) => set('sort_order', Number(e.target.value))}
                className="w-24"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="is_active">Aktif</Label>
              <Switch id="is_active" checked={values.is_active} onCheckedChange={(v) => set('is_active', v)} />
            </div>
          </div>

          {error && <p className="text-sm text-[hsl(var(--destructive))]">{error}</p>}

          <Button type="submit" disabled={saving || uploading}>
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ProjectForm;
